import React, { useState } from 'react';

interface JsonViewerProps {
  data: unknown;
  defaultExpanded?: boolean;
  maxDepth?: number;
  rootLabel?: string;
}

const INDENT_PX = 16;

const JsonViewer: React.FC<JsonViewerProps> = ({
  data,
  defaultExpanded = true,
  maxDepth = 6,
  rootLabel,
}) => {
  return (
    <div className="font-mono text-[12px] leading-[1.6] bg-gray-50 border border-gray-200 rounded-lg px-4 py-3 overflow-x-auto">
      {rootLabel && (
        <div className="text-[10px] uppercase tracking-wider text-gray-400 font-sans font-medium mb-1.5">
          {rootLabel}
        </div>
      )}
      <JsonNode value={data} depth={0} defaultExpanded={defaultExpanded} maxDepth={maxDepth} />
    </div>
  );
};

const JsonNode: React.FC<{
  value: unknown;
  depth: number;
  defaultExpanded: boolean;
  maxDepth: number;
  isLast?: boolean;
}> = ({ value, depth, defaultExpanded, maxDepth, isLast = true }) => {
  if (value === null) return <Primitive value="null" color="text-gray-400" isLast={isLast} />;
  if (value === undefined) return <Primitive value="undefined" color="text-gray-400" isLast={isLast} />;
  if (typeof value === 'boolean') return <Primitive value={String(value)} color="text-amber-600" isLast={isLast} />;
  if (typeof value === 'number') return <Primitive value={String(value)} color="text-blue-600" isLast={isLast} />;
  if (typeof value === 'string') return <StringValue value={value} isLast={isLast} />;
  if (Array.isArray(value)) {
    return (
      <CollapsibleBracket
        openBracket="["
        closeBracket="]"
        items={value}
        depth={depth}
        defaultExpanded={defaultExpanded}
        maxDepth={maxDepth}
        isLast={isLast}
        renderItem={(item, idx, last) => (
          <div key={idx} style={{ paddingLeft: INDENT_PX }}>
            <JsonNode value={item} depth={depth + 1} defaultExpanded={defaultExpanded} maxDepth={maxDepth} isLast={last} />
          </div>
        )}
      />
    );
  }
  if (typeof value === 'object') {
    const entries = Object.entries(value as Record<string, unknown>);
    return (
      <CollapsibleBracket
        openBracket="{"
        closeBracket="}"
        items={entries}
        depth={depth}
        defaultExpanded={defaultExpanded}
        maxDepth={maxDepth}
        isLast={isLast}
        renderItem={([key, val], _idx, last) => (
          <div key={key} style={{ paddingLeft: INDENT_PX }} className="flex">
            <span className="text-gray-500 select-none">{key}</span>
            <span className="text-gray-400 select-none mr-1.5">:</span>
            <span className="flex-1 min-w-0">
              <JsonNode value={val} depth={depth + 1} defaultExpanded={defaultExpanded} maxDepth={maxDepth} isLast={last} />
            </span>
          </div>
        )}
      />
    );
  }
  return <span className="text-gray-600">{String(value)}</span>;
};

const Primitive: React.FC<{ value: string; color: string; isLast: boolean }> = ({ value, color, isLast }) => (
  <span>
    <span className={color}>{value}</span>
    {!isLast && <span className="text-gray-300">,</span>}
  </span>
);

const StringValue: React.FC<{ value: string; isLast: boolean }> = ({ value, isLast }) => {
  const isLong = value.length > 120;
  const [expanded, setExpanded] = useState(false);

  const display = isLong && !expanded ? value.slice(0, 120) + '…' : value;

  return (
    <span className="inline">
      <span className="text-teal-700">"</span>
      <span className="text-teal-700 break-all whitespace-pre-wrap">{display}</span>
      <span className="text-teal-700">"</span>
      {isLong && (
        <button
          onClick={() => setExpanded(!expanded)}
          className="ml-1 text-[10px] text-gray-400 hover:text-teal-600 font-sans transition-colors"
        >
          {expanded ? 'less' : `+${value.length - 120} chars`}
        </button>
      )}
      {!isLast && <span className="text-gray-300">,</span>}
    </span>
  );
};

const CollapsibleBracket: React.FC<{
  openBracket: string;
  closeBracket: string;
  items: any[];
  depth: number;
  defaultExpanded: boolean;
  maxDepth: number;
  isLast: boolean;
  renderItem: (item: any, index: number, isLast: boolean) => React.ReactNode;
}> = ({ openBracket, closeBracket, items, depth, defaultExpanded, maxDepth, isLast, renderItem }) => {
  const shouldStartExpanded = depth < 2 ? defaultExpanded : items.length <= 3;
  const [expanded, setExpanded] = useState(shouldStartExpanded && depth < maxDepth);

  if (items.length === 0) {
    return (
      <span>
        <span className="text-gray-500">{openBracket}{closeBracket}</span>
        {!isLast && <span className="text-gray-300">,</span>}
      </span>
    );
  }

  if (!expanded) {
    return (
      <span className="inline-flex items-baseline gap-0.5">
        <button
          onClick={() => setExpanded(true)}
          className="text-gray-400 hover:text-gray-700 transition-colors"
          title="Expand"
        >
          <span className="text-gray-500">{openBracket}</span>
          <span className="text-[10px] text-gray-400 mx-0.5">{items.length} item{items.length > 1 ? 's' : ''}</span>
          <span className="text-gray-500">{closeBracket}</span>
        </button>
        {!isLast && <span className="text-gray-300">,</span>}
      </span>
    );
  }

  return (
    <span>
      <button
        onClick={() => setExpanded(false)}
        className="text-gray-500 hover:text-gray-700 transition-colors"
        title="Collapse"
      >
        {openBracket}
      </button>
      <div>
        {items.map((item, idx) => renderItem(item, idx, idx === items.length - 1))}
      </div>
      <span className="text-gray-500">{closeBracket}</span>
      {!isLast && <span className="text-gray-300">,</span>}
    </span>
  );
};

export default JsonViewer;
